<script lang="ts">
export default {
  name: 'StoreConnectGmb',
}
</script>
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { storeToRefs } from 'pinia'
import { useLocationConnectStore } from '../../domains/location/stores'
import {
  KkButton,
  KkFormLabel,
  KkTextField,
  KkRadioGroup,
  KkSelect,
  KkValidationError,
  KkBox,
  KkLoading,
  KkTooltip,
} from '../../components/base'
import {
  KkInfo,
} from '../../components/ui'
// TODO: コーディング規約に合わせてリファクタ
import Autocomplete from '../../components/Autocomplete.vue'
import ConfirmationModal from '../../components/CompaniesLocationStore/ConfirmationModal.vue'
import { siteInfoMap } from 'src/biz_spa/modules/sitesInfoMapConst'

/** Interface */
interface ReadLocationStoreParams {
  company_uuid: string
  store_uuid: string
}
interface ReadStoreGmbParams {
  company_uuid: string
  store_uuid: string
}
interface CreateStoreGmbParams {
  company_uuid: string
  store_uuid: string
  payload: {
    store_gmb: {
      location: string
    }
  }
}
interface ReadStoreGmbLocationsParams {
  companyUuid: string
  accountId: string
}
interface BusinessGroup {
  [key: string]: string
}
interface GmbAccount {
  id: string
  type: string
  attributes: {
    name: string
  }
}
interface GmbLocation {
  id: string
  type: string
  attributes: {
    name: string
    connected: boolean
    store_code?: string
  }
}
interface SelectedGmbLocation {
  id: string
  name: string
}
interface FilteredGmbLocation {
  id: string
  name: string
  $isDisabled?: boolean
}

/** Variable */
const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const emit = defineEmits(['update-title'])
const isConfirmationModal = ref<boolean>(false)

/** Computed */
const companyUuid = computed<string>(() => route.params.company_uuid as string)
const storeUuid = computed<string>(() => route.params.store_uuid as string)
const businessGroups = computed<BusinessGroup>(() => {
  if (!gmbAccounts.value) return {}
  const obj = {} as BusinessGroup
  gmbAccounts.value.forEach(gmbAccount => {
    obj[gmbAccount.attributes.name] = gmbAccount.id
  })
  return obj
})
const filteredGmbLocations = computed<FilteredGmbLocation[]>(() => {
  if (!gmbLocations.value) return []
  return gmbLocations.value.map(location => {
    const storeCode = location.attributes.store_code
    const obj = {
      id: location.id,
      name: location.attributes.name + (storeCode ? ` (${storeCode})` : ''),
    } as FilteredGmbLocation
    if (location.attributes.connected) { obj.$isDisabled = true }
    return obj
  })
})
const submittable = computed<boolean>(() => {
  const { dirty, valid } = meta.value

  if (dirty && valid && !isLoading.value && !isWriting.value && !locationStoreLoading.value) {
    if (connectType.value === 'gmb') {
      return Boolean(businessGroup.value && gmbLocation.value)
    } else if (connectType.value === 'url') {
      return Boolean(url.value)
    } else {
      return false
    }
  } else {
    return false
  }
})

/** Form */
const { handleSubmit, meta } = useForm({
  validationSchema: {
    connectType: 'required',
    url: 'google_url',
  },
  initialValues: {
    connectType: 'gmb',
    url: '',
  },
})

const isHoveredGoogleUrlDescription = ref<boolean>(false)

const {
  value: connectType,
  handleChange: updateConnectType
} = useField<string>('connectType', undefined)

const {
  value: businessGroup,
  handleChange: updateBusinessGroup
} = useField<string>('businessGroup', undefined, { label: t('pages.LocationStoreConnectGmb.form.labels.businessGroup') })

const {
  value: gmbLocation,
  handleChange: updateGmbLocation
} = useField<SelectedGmbLocation>('gmbLocation', undefined, { label: t('pages.LocationStoreConnectGmb.form.labels.gmbLocation') })

const {
  value: url,
  errorMessage: urlError,
  handleChange: updateUrl
} = useField<string>('url', undefined, { label: t('pages.LocationStoreConnectGmb.form.labels.url') })

/** Pinia Store */
const locationConnectStore = useLocationConnectStore()
const { connect, isLoading, isWriting } = storeToRefs(locationConnectStore)
const { readConnect, createConnect, updateConnect } = locationConnectStore

/** Vuex Store: state */
const companyLoading = computed<boolean>(() => store.state.companiesCompany.loading)
const locationStore = computed(() => store.state.companiesLocationStore.data)
const locationStoreLoading = computed<boolean>(() => store.state.companiesLocationStore.loading)
const gmbAccountsLoading = computed<boolean>(() => store.state.companiesGmbAccounts.loading)
const gmbAccounts = computed<GmbAccount[]>(() => store.state.companiesGmbAccounts.data?.data)
const storeGmbLoading = computed<boolean>(() => store.state.companiesStoreGmb.loading)
const storeGmbConnected = computed<boolean>(() => store.state.companiesStoreGmb.connected)
const gmbLocations = computed<GmbLocation[]>(() => store.state.companiesStoreGmbLocations.data)
const gmbLocationsLoading = computed<GmbLocation[]>(() => store.state.companiesStoreGmbLocations.loading)

/** Vuex Store: getters */
const hasGmbAccount = computed<boolean>(() => store.getters['companiesCompany/hasGmbAccount'])
const directSelling = computed<boolean>(() => store.getters['companiesCompany/directSelling'])
const isRettyDistributer = computed<boolean>(() => store.getters['companiesCompany/isRettyDistributer'])
const storeGmbName = computed<boolean>(() => store.getters['companiesStoreGmb/storeName'])
const isAdmin = computed<boolean>(() => store.getters['companiesAccessibleApi/isAdmin'])
const isDistributer = computed<boolean>(() => store.getters['companiesAccessibleApi/isDistributer'])
const showMoreThanEditorAuthority = computed<boolean>(() => store.getters['companiesAccessibleApi/showMoreThanEditorAuthority'])

/** Vuex Store: action */
const readLocationStore = (payload: ReadLocationStoreParams) => store.dispatch('companiesLocationStore/readStore', payload)
const readBusinessGroups = (companyUuid: string) => store.dispatch('companiesGmbAccounts/readCompaniesGmbAccounts', companyUuid)
const readStoreGmb = (payload: ReadStoreGmbParams) => store.dispatch('companiesStoreGmb/readStoreGmb', payload)
const createStoreGmb = (payload: CreateStoreGmbParams) => store.dispatch('companiesStoreGmb/createStoreGmb', payload)
const resetGmbLocation = () => store.dispatch('companiesStoreGmb/resetLocation')
const readStoreGmbLocations = (payload: ReadStoreGmbLocationsParams) => store.dispatch('companiesStoreGmbLocations/readStoreGmbLocations', payload)

/** Lifecycle */
readLocationStore({ company_uuid: companyUuid.value, store_uuid: storeUuid.value })
readBusinessGroups(companyUuid.value)
readStoreGmb({ company_uuid: companyUuid.value, store_uuid: storeUuid.value })
readConnect({ companyUuid: companyUuid.value, storeUuid: storeUuid.value, siteType: 'google' })

/** Watch */
watch(locationStore, () => {
  if (!locationStore.value) return
  emit('update-title', t('pages.LocationStoreConnectGmb.title', { name: locationStore.value.attributes.name }))
})
watch(connect, () => {
  if (!connect.value.review.url) return
  url.value = connect.value.review.url
})
watch([connect, storeGmbConnected, directSelling, isRettyDistributer, isAdmin, isDistributer], () => {
  if (!storeGmbConnected.value && connect.value.review.url) {
    connectType.value = 'url'
  }
  if ((directSelling.value || isRettyDistributer.value) && !isAdmin.value && !isDistributer.value ) {
    connectType.value = 'url'
  }
})
watch(businessGroup, () => {
  if (!businessGroup.value) return
  resetGmbLocation()
  readStoreGmbLocations({
    companyUuid: companyUuid.value,
    accountId: businessGroup.value,
  })
})

/** Methods */
const redirect = () => {
  router.push({
    name: 'LocationStoreConnects',
    params: { company_uuid: companyUuid.value, store_uuid: storeUuid.value },
  })
}

const onSubmit = handleSubmit(values => {
  if (values.connectType === 'gmb' ) {
    isConfirmationModal.value = true
  } else if (values.connectType === 'url' ) {
    const params = {
      companyUuid: companyUuid.value,
      storeUuid: storeUuid.value,
      siteType: 'google',
      params: {
        review: {
          url: values.url,
        },
      }
    }
    if (connect.value.review?.url) {
      updateConnect(params).then(() => redirect())
    } else {
      createConnect(params).then(() => redirect())
    }
  }
})
const hideConfirmationModal = () => {
  isConfirmationModal.value = false
}
const connectGmbAccount = () => {
  isConfirmationModal.value = false
  createStoreGmb({
    company_uuid: companyUuid.value,
    store_uuid: storeUuid.value,
    payload: {
      store_gmb: {
        location: gmbLocation.value.id
      },
    }
  }).then(() => redirect())
}
</script>

<template>
  <form class="storeGmbConnect" @submit.prevent="onSubmit">
    <KkBox tag="div">
      <template #content>
        <div class="storeGmbConnect__container">
          <KkBox
            tag="div"
            border
            :title="t('pages.LocationStoreConnectGmb.basicTitle')"
            :note="((directSelling || isRettyDistributer) && isAdmin) ? t('pages.LocationStoreConnectGmb.onlyAdmin') : undefined"
          >
            <template #content>
              <!-- 販売店mov配下かRetty配下の会社の場合かつadminアカウントでも販売店アカウントでもない場合 -->
              <template v-if="(directSelling || isRettyDistributer) && !isAdmin && !isDistributer">
                <p class="storeGmbConnect__text">{{ t('pages.LocationStoreConnectGmb.form.connectUrl') }}</p>
                <KkFormLabel :text="t('pages.LocationStoreConnectGmb.form.labels.urlCount')" />
                <KkTextField
                  :model-value="url"
                  :placeholder="t('messages.noSettings')"
                  @update:model-value="updateUrl"
                />
                <KkValidationError :text="urlError" />
              </template>
              <!-- それ以外の場合 -->
              <template v-else>
                <div class="storeGmbConnect__info">
                  <KkInfo icon="info">
                    {{ t('pages.LocationStoreConnectGmb.note') }}
                  </KkInfo>
                </div>
                <!-- GMB 連携 -->
                <div class="storeGmbConnect__radio">
                  <KkRadioGroup
                    :model-value="connectType"
                    :items="[
                      {
                        id: 1,
                        value: 'gmb',
                        text: t('pages.LocationStoreConnectGmb.form.connectGmb'),
                      }
                    ]"
                    name="connectType"
                    @update:model-value="updateConnectType"
                  />
                  <div v-if="connectType === 'gmb'" class="storeGmbConnect__gmb">
                    <KkLoading
                      v-if="gmbAccountsLoading || storeGmbLoading || companyLoading"
                      min-height="120px"
                    />
                    <template v-else>
                      <!-- GMB アカウント連携済み -->
                      <template v-if="hasGmbAccount">
                        <!-- 店舗連携済み -->
                        <template v-if="storeGmbConnected">
                          <p>{{ $t('pages.LocationStoreConnectGmb.form.connectedWith', { name: storeGmbName }) }}</p>
                        </template>
                        <!-- 店舗未連携 -->
                        <template v-else>
                          <div class="storeGmbConnect__gmb__item">
                            <KkSelect
                              :options="businessGroups"
                              :model-value="businessGroup"
                              :placeholder="t('pages.LocationStoreConnectGmb.form.selectBusinessGroup')"
                              :disabled="!showMoreThanEditorAuthority"
                              @update:model-value="updateBusinessGroup"
                            />
                          </div>
                          <div v-if="businessGroup" class="storeGmbConnect__gmb__item">
                            <KkLoading
                              v-if="gmbLocationsLoading"
                              min-height="120px"
                            />
                            <Autocomplete
                              v-else
                              :id="'store_gmb'"
                              :label="'name'"
                              :track-by="'id'"
                              :data-list="filteredGmbLocations"
                              :placeholder="t('pages.LocationStoreConnectGmb.form.selectStoreName')"
                              :value="gmbLocation"
                              @change="updateGmbLocation"
                            />
                          </div>
                        </template>
                      </template>
                      <!-- GMB アカウント未連携 -->
                      <template v-else>
                        <p class="storeGmbConnect__gmb__link">
                          <router-link :to="{ name: 'CompaniesGoogleOauth', params: { company_uuid: companyUuid }}">
                            {{ t('pages.LocationStoreConnectGmb.confirmGmbConnect') }}
                          </router-link>
                        </p>
                      </template>
                    </template>
                  </div>
                </div>
                <!-- URL 連携 -->
                <div v-if="!storeGmbConnected" class="storeGmbConnect__radio">
                  <KkRadioGroup
                    :model-value="connectType"
                    :items="[
                      {
                        id: 2,
                        value: 'url',
                        text: t('pages.LocationStoreConnectGmb.form.connectUrl'),
                      }
                    ]"
                    name="connectType"
                    @update:model-value="updateConnectType"
                  />
                  <div v-if="connectType === 'url'" class="storeGmbConnect__url">
                    <KkTooltip
                      :is-visible="isHoveredGoogleUrlDescription"
                      top="25px"
                      width="500px"
                      :label="$t('pages.LocationStoreConnectGmb.form.labels.urlCount')"
                      font-weight="bold"
                      font-size="12px"
                      class="formLabel"
                      @showTooltip="isHoveredGoogleUrlDescription=true"
                      @hideTooltip="isHoveredGoogleUrlDescription=false"
                    >
                      <p>{{ $t('pages.LocationStoreConnectAccount.info.exampleUrl') }}</p>
                      <ul>
                        <li v-for="sampleUrl in siteInfoMap['google']['sampleURLs']" :key="sampleUrl" class="sampleUrls">
                          {{ sampleUrl }}
                        </li>
                      </ul>
                    </KkTooltip>
                    <KkTextField
                      :model-value="url"
                      :placeholder="t('messages.noSettings')"
                      @update:model-value="updateUrl"
                    />
                    <KkValidationError :text="urlError" />
                  </div>
                </div>
              </template>
            </template>
          </KkBox>
        </div>
      </template>
      <template #footer>
        <div class="storeGmbConnect__buttons">
          <KkButton
            color="secondary"
            :to="{ name: 'LocationStoreConnects', params: { company_uuid: companyUuid, store_uuid: storeUuid } }"
            :text="t('labels.cancel')"
          />
          <KkButton
            type="submit"
            color="primary"
            :text="t('labels.save')"
            :disabled="!submittable"
          />
        </div>
      </template>
    </KkBox>
  </form>
  <ConfirmationModal
    :show-confirmation-modal="isConfirmationModal"
    @on-submit="connectGmbAccount"
    @close-modal="hideConfirmationModal"
  />
</template>

<style lang="scss" scoped>
@import "../../../../stylesheets/biz_spa/foundation/var";
@import "../../../../stylesheets/biz_spa/foundation/mixin";
.storeGmbConnect {
  $root: #{&};
  &__container {
    margin-bottom: 300px;
  }
  &__buttons {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
  }
  &__info {
    margin-bottom: 16px;
  }
  &__radio {
    + & {
      margin-top: 24px;
    }
  }
  &__text {
    margin-bottom: 16px;
    font-size: 12px;
  }
  &__url,
  &__gmb {
    margin-top: 10px;
  }
  &__gmb {
    &__link {
      font-size: 12px;
      line-height: 1.5;
      a {
        color: #E9446A;
      }
    }
    &__item {
      max-width: 400px;
      + & {
        margin-top: 10px;
      }
    }
  }
}
.sampleUrls {
  word-wrap: break-word;
}
</style>
